import React, { useEffect, useState } from "react";
import { DataTable, Modal, P, Spinner } from "@tag/tag-components-react-v4";
import { CustomerDTO } from "../types/CustomerDTO";
import CustomerService from "../services/CustomerService";
import { CareConnectConfigurationDTO } from "../types/CareConnectConfigurationDTO";
import { DEFAULT_DAILY_EMAIL_PROCESSING_LIMIT } from "./CareConnectConfigurationModal/CareConnectConfigurationModal";
import { ProjectAccents } from "../../General/config/projectAccents";

function CareConnectConfigurationDetails(props: { visible: boolean, selectedCustomer: CustomerDTO, handleClose: Function }) {

    const [configuration, setConfiguration] = useState<CareConnectConfigurationDTO | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);

    useEffect(() => {
        let isMounted = true;

        const fetchConfiguration = async () => {
            try {
                setLoading(true);
                const data = await CustomerService.getCareConnectConfiguration(
                    props.selectedCustomer.clientId,
                    props.selectedCustomer.environmentName
                );

                if (isMounted) {
                    setConfiguration(data);
                    setLoadFailed(false);
                }
            } catch (error) {
                console.error("Error fetching Care Connect configuration:", error);
                if (isMounted) {
                    setLoadFailed(true);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchConfiguration();

        return () => {
            isMounted = false;
        };
    }, [props.selectedCustomer.clientId, props.selectedCustomer.environmentName]);

    const columns = React.useMemo(
        () => [
            { accessorKey: 'displayName', header: 'Property' },
            { accessorKey: 'value', header: 'Value' }
        ],
        [],
    );

    const data = configuration ? [
        {
            key: 'edition',
            displayName: 'Edition',
            value: configuration.isLiteEdition ? 'Lite' : 'Full'
        },
        {
            key: 'dailyEmailProcessingLimit',
            displayName: 'Daily Email Processing Limit',
            value: (configuration.dailyEmailProcessingLimit ?? DEFAULT_DAILY_EMAIL_PROCESSING_LIMIT).toString()
        }
    ] : [];

    const renderModalContent = () => {
        if (loading) {
            return <Spinner size="medium" />;
        }

        if (loadFailed) {
            return <P>Sorry, the Care Connect configuration could not be loaded.</P>;
        }

        return (
            <>
                <DataTable
                    columns={columns}
                    data={data}
                    noDataElement={<P>Sorry, there is no Care Connect configuration available.</P>}
                />
                {configuration && !configuration.hasConfiguration &&
                    <P>This customer has no Care Connect configuration record, so these are the default values.</P>
                }
            </>
        );
    };

    return (
        <Modal
            visible={props.visible}
            accent={ProjectAccents.PeoplePlanner}
            heading={`Care Connect Configuration`}
            primaryButtonProps={{ buttonStyle: { 'display': 'none' } }}
            onCancelButtonClick={() => {
                props.handleClose();
            }}
            onClose={() => props.handleClose()}
        >
            <div style={{ marginTop: '16px' }}>
                {renderModalContent()}
            </div>
        </Modal>
    )

}

export default CareConnectConfigurationDetails;
